/**
 * The one control per-device settings are built from.
 *
 * A section is in exactly one of two states — following the family defaults,
 * or customised for this device — with exactly one action available in each.
 * That binary is only possible because overrides are section-level: there is
 * no "partly customised" to render, so a parent can always answer "what is
 * this device actually running?" at a glance.
 *
 * Customised sections carry the poppy left rail, the same treatment the
 * dashboard already uses for its "Encryption locked" card.
 */

import type { ComponentChildren } from 'preact';
import { cn } from '../lib/cn';

interface Props {
  /** Section name, rendered as the eyebrow. */
  label: string;
  /** True when this device overrides the family value. */
  overridden: boolean;
  /** The value in force on this device. */
  summary: ComponentChildren;
  /**
   * The family value, shown underneath when overridden so the parent can see
   * what they diverged from without navigating away.
   */
  familySummary?: ComponentChildren;
  /** Switch this section to a device-specific value. */
  onCustomise: () => void;
  /** Drop the override and follow the family default again. */
  onReset: () => void;
  /** Disables both actions (mid-save). */
  busy?: boolean;
  /** The expanded editor, rendered only while overridden. */
  children?: ComponentChildren;
}

export function InheritSection({
  label,
  overridden,
  summary,
  familySummary,
  onCustomise,
  onReset,
  busy = false,
  children,
}: Props) {
  return (
    <div
      class={cn(
        'flex overflow-hidden rounded-lg border border-surface-muted/20 bg-surface',
        overridden && 'border-signal/30',
      )}
    >
      {/* Rail is decorative — the "Custom" wording carries the meaning for
          anyone who can't see it. */}
      <div
        aria-hidden="true"
        class={cn('w-1 shrink-0 self-stretch', overridden && 'bg-signal')}
      />
      <div class="min-w-0 flex-1 p-4">
        <div class="flex flex-wrap items-baseline justify-between gap-2">
          <span class="font-mono text-[11px] tracking-[0.14em] uppercase text-surface-muted">
            {label}
            {overridden ? (
              <span class="ml-2 text-signal">· Custom</span>
            ) : (
              <span class="ml-2 text-surface-muted/70">· Inherited</span>
            )}
          </span>
          <button
            type="button"
            disabled={busy}
            onClick={overridden ? onReset : onCustomise}
            class={cn(
              'font-mono text-[11px] tracking-[0.14em] uppercase transition-colors',
              'disabled:opacity-50',
              overridden
                ? 'text-signal hover:text-signal/80'
                : 'text-surface-muted hover:text-surface-fg',
            )}
          >
            {overridden ? 'Reset to family default' : 'Customise'}
          </button>
        </div>

        <div
          class={cn(
            'mt-1 text-[15px]',
            overridden ? 'font-medium' : 'text-surface-body',
          )}
        >
          {summary}
        </div>

        {overridden && familySummary !== undefined ? (
          <div class="mt-1 text-xs text-surface-muted">
            Family default: {familySummary}
          </div>
        ) : null}
        {!overridden ? (
          <div class="mt-1 text-xs text-surface-muted">
            From family defaults. Changes there apply here.
          </div>
        ) : null}

        {overridden && children !== undefined ? (
          <div class="mt-4 border-t border-surface-muted/20 pt-4">
            {children}
          </div>
        ) : null}
      </div>
    </div>
  );
}
